import ParallaxEvent from '../../../components/parallax/parallax-event';
import Video from '../../../components/video/script';

import { multiAddEventListener, multiRemoveEventListener } from '../../../assets/js/helpers';

// constants
import CursorEvent from '../../../bits/cursor/cursor-event';
import SkrolliteEvent from '../../../components/skrollite/skrollite-event';
import SliderDraggableEvent from '../../../components/slider-draggable/slider-draggable-event';
// import VideoEvent from '../../../assets/js/constants/video-event';

export default class VideoPlaylist {

	static selector = 'gallery-video-playlist';

	// class variables

	components = {
		videos: []
	};

	coords = {
		contentWrapper: {
			height: null
		}
	};

	el = {};

	flags = {
		isCursorEnabled: true
	};

	states = {
		activeIndex: 0,
		activeVideo: null,
		activeThumbnail: null,
		total: 0
	}

	// constructor

	constructor(container) {
		this.el = {
			container,

			// singles
			contentWrapper: container.querySelector(`.${this.constructor.selector}__content`),
			cursor: container.querySelector(`.${this.constructor.selector}__cursor`),
			slider: container.querySelector('[data-slider-draggable]'),

			// arrays
			parallaxes: container.querySelectorAll(`.${this.constructor.selector}__content-item-wrapper div[data-parallax]`),
			thumbnails: container.querySelectorAll(`.${this.constructor.selector}__thumbnail`),
			videos: container.querySelectorAll(`.${this.constructor.selector}__content-item-video`),
			videoWrappers: container.querySelectorAll(`.${this.constructor.selector}__content-item-video-wrapper`)
		};

		this.states.total = this.el.videos.length;

		this.initialize();
	}

	// initialize

	initialize() {
		this.#addVideos();
		this.#addEventListeners();
		this.renderActiveItemByIndex(0);
	}

	// add and remove listeners

	#addEventListeners = () => {
		// when offscreen pause current video
		this.el.contentWrapper.addEventListener(SkrolliteEvent.WINDOW_IN, this.#onContentWrapperWindowIn);
		this.el.contentWrapper.addEventListener(SkrolliteEvent.WINDOW_OUT, this.#onContentWrapperWindowOut);

		// check whether clicking anywhere in slider area will trigger an edge slide change
		this.el.slider.addEventListener('click', this.#onSliderClick);

		// listen for changes in the slider
		this.el.slider.addEventListener(SliderDraggableEvent.SEEKING, this.#onSliderSeeking);
		this.el.slider.addEventListener(SliderDraggableEvent.DRAG_START, this.#onSliderDragStart);

		// listen for thumbnail interaction
		multiAddEventListener(this.el.thumbnails, 'click', this.#onThumbnailClick);
		multiAddEventListener(this.el.thumbnails, 'mouseenter', this.#onThumbnailOver);
		multiAddEventListener(this.el.thumbnails, 'mouseleave', this.#onThumbnailOut);
	}

	#removeEventListeners = () => {
		this.el.contentWrapper.removeEventListener(SkrolliteEvent.WINDOW_IN, this.#onContentWrapperWindowIn);
		this.el.contentWrapper.removeEventListener(SkrolliteEvent.WINDOW_OUT, this.#onContentWrapperWindowOut);

		this.el.slider.removeEventListener('click', this.#onSliderClick);
		this.el.slider.removeEventListener(SliderDraggableEvent.SEEKING, this.#onSliderSeeking);
		this.el.slider.removeEventListener(SliderDraggableEvent.DRAG_START, this.#onSliderDragStart);

		multiRemoveEventListener(this.el.thumbnails, 'click', this.#onThumbnailClick);
		multiRemoveEventListener(this.el.thumbnails, 'mouseenter', this.#onThumbnailOver);
		multiRemoveEventListener(this.el.thumbnails, 'mouseleave', this.#onThumbnailOut);
	}

	// listener methods
	#onContentWrapperWindowIn = () => {
		this.activeVideoPlay();
	}

	#onContentWrapperWindowOut = () => {
		this.activeVideoPause();
	}

	#onSliderClick = () => {
		if (this.flags.isCursorEnabled) {
			if (this.el.cursor.classList.contains('is-edge-left')) {
				this.el.slider.dispatchEvent(new Event(SliderDraggableEvent.PREV));
			} else if (this.el.cursor.classList.contains('is-edge-right')) {
				this.el.slider.dispatchEvent(new Event(SliderDraggableEvent.NEXT));
			}
		}
	}

	#onSliderSeeking = (e) => {
		this.renderActiveItemByIndex(e.detail.index);
	}

	#onSliderDragStart = () => {
		for (let i = 0; i < this.components.videos.length; i++) {
			const video = this.components.videos[i];
			video.pause();
		}

		for (let i = 0; i < this.el.parallaxes.length; i++) {
			const $parallax = this.el.parallaxes[i];
			if (i !== this.states.index) {
				$parallax.dispatchEvent(new Event(ParallaxEvent.RENDER));
			}
		}
	}

	#onThumbnailClick = (e) => {
		const $target = e.currentTarget;
		this.setActiveItemByIndex($target.dataset.index);
	}

	#onThumbnailOver = () => {
		this.cursorHide();
	}

	#onThumbnailOut = () => {
		this.cursorShow();
	}

	// change active item

	setActiveItemByIndex(index) {
		if (!isNaN(parseInt(index))) {
			this.el.slider.dispatchEvent(new CustomEvent(SliderDraggableEvent.SEEK, { detail: { index } }));
		}
	}

	renderActiveItemByIndex(index) {
		if (!isNaN(parseInt(index))) {

			const lastIndex = this.states.index;
			const $lastActiveThumbnail = this.states.activeThumbnail;
			const lastActiveVideo = this.states.activeVideo;

			const isFirst = !lastActiveVideo;

			this.states.index = parseInt(index);
			this.states.activeThumbnail = this.el.thumbnails[index];
			this.states.activeVideo = this.components.videos[index];

			if (this.states.index !== lastIndex || isFirst) {

				for (let i = 0; i < this.components.videos.length; i++) {
					const video = this.components.videos[i];
					if (video !== this.states.activeVideo) {
						video.pause();
					}
				}

				for (let i = 0; i < this.el.parallaxes.length; i++) {
					const $parallax = this.el.parallaxes[i];
					if (i !== this.states.index) {
						$parallax.dispatchEvent(new Event(ParallaxEvent.DISABLE));
					}
				}

				if (!isFirst) {
					$lastActiveThumbnail.classList.remove('is-active');
				}

				if (this.states.index === 0) {
					this.el.cursor.dispatchEvent(new Event(CursorEvent.DISABLE_LEFT));
				} else {
					this.el.cursor.dispatchEvent(new Event(CursorEvent.ENABLE_LEFT));
				}

				if (this.states.index === this.states.total - 1) {
					this.el.cursor.dispatchEvent(new Event(CursorEvent.DISABLE_RIGHT));
				} else {
					this.el.cursor.dispatchEvent(new Event(CursorEvent.ENABLE_RIGHT));
				}

				this.el.parallaxes[this.states.index].dispatchEvent(new Event(ParallaxEvent.ENABLE));

				this.states.activeThumbnail.classList.add('is-active');
			}

			// load video if not already loaded

			this.states.activeVideo.play();
		}
	}

	nextVideo() {
		let nextIndex = this.states.index + 1;
		if (nextIndex >= this.states.total) {
			nextIndex = 0;
		}
		this.setActiveItemByIndex(nextIndex);
	}

	// cursor

	cursorHide() {
		this.flags.isCursorEnabled = false;
		this.el.cursor.dispatchEvent(new Event(CursorEvent.HIDE));
	}

	cursorShow() {
		this.flags.isCursorEnabled = true;
		this.el.cursor.dispatchEvent(new Event(CursorEvent.SHOW));
	}

	// video

	#onVideoLoadStart = () => {
		if (this.states.activeVideo.video.src) {
			this.states.activeVideo.video.removeEventListener('loadstart', this.#onVideoLoadStart);
			this.states.activeVideo.play();
		}
	}

	#addVideos = () => {
		this.#removeVideos();
		for (let i = 0; i < this.el.videos.length; i++) {
			const $video = this.el.videos[i];
			const video = new Video($video);
			video.load(false);
			this.components.videos.push(video);
		}
	}

	#removeVideos = () => {
		if (this.components.videos.length) {
			for (let i = 0; i < this.components.videos.length; i++) {
				const video = this.components.videos[i];
				video.destroy();
			}
			this.components.videos = [];
		}
	}

	activeVideoPause() {
		if (this.states.activeVideo) {
			this.states.activeVideo.pause();
		}
	}

	activeVideoPlay() {
		if (this.states.activeVideo) {
			this.states.activeVideo.play();
		}
	}

	// destroy

	destroy() {
		this.#removeVideos();
		this.#removeEventListeners();
	}

}
